import { useRef, useMemo } from 'react'
import { useSliderState } from '@react-stately/slider'
import { useSlider, useSliderThumb } from '@react-aria/slider'
import { useFocusRing } from '@react-aria/focus'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { mergeProps } from '@react-aria/utils'
import { Text } from 'blaze-ui/Text/Text'
import { useNumberFormatter } from '@react-aria/i18n'
import { VolumeOff, VolumeOn, Trash2, Plus, Edit1 } from 'blaze-ui/icons'
import Styles from './AudioEditorModal.module.scss'
import cx from 'classnames'
import type { AudioAsset } from 'types'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Button } from 'blaze-ui/Button/Button'
import { useVideoFrameExtraction } from './hooks/useVideoFrameExtraction'
import { useMusicTrackDragging } from './hooks/useMusicTrackDragging'
import { Tooltip } from 'components/Tooltip/Tooltip'

interface TimelineEditorProps {
  videoUrl: string
  videoDuration: number
  currentTime: number
  onTimeChange: (time: number) => void
  selectedAudio?: AudioAsset | null
  musicStartTime: number
  onMusicStartTimeChange: (time: number) => void
  onPausePlayback?: () => void
  onResumePlayback?: () => void
  onReplaceAudio: () => void
  onRemoveAudio: () => void
  isPlaying?: boolean
  isVideoMuted?: boolean
  onToggleVideoMute?: () => void
  hasOriginalAudio?: boolean
  hasExistingMusicTrack?: boolean
  hasNativeAudioConfig?: boolean
}

const Waveform = () => {
  const bars = useMemo(() => {
    return Array.from({ length: 120 }).map((_, i) => {
      const height = i % 2 === 0 ? 25 : 15
      return { height }
    })
  }, [])

  return (
    <div className={Styles.timelineWaveform}>
      {bars.map((bar, i) => (
        <div
          key={i}
          className={Styles.timelineWaveformBar}
          style={{
            height: `${bar.height}px`,
          }}
        />
      ))}
    </div>
  )
}

const Thumb = ({ state, trackRef, index }: { state: any; trackRef: any; index: number }) => {
  const inputRef = useRef(null)
  const { thumbProps, inputProps, isDragging } = useSliderThumb(
    {
      index,
      trackRef,
      inputRef,
    },
    state,
  )

  const { focusProps, isFocusVisible } = useFocusRing()

  return (
    <div
      {...thumbProps}
      className={cx(Styles.playhead, {
        [Styles.focus]: isFocusVisible,
        [Styles.dragging]: isDragging,
      })}
    >
      <VisuallyHidden>
        <input ref={inputRef} {...mergeProps(inputProps, focusProps)} />
      </VisuallyHidden>
    </div>
  )
}

export const TimelineEditor = ({
  videoUrl,
  videoDuration,
  currentTime,
  onTimeChange,
  selectedAudio,
  musicStartTime,
  onMusicStartTimeChange,
  onPausePlayback,
  onResumePlayback,
  onReplaceAudio,
  onRemoveAudio,
  isPlaying = false,
  isVideoMuted = false,
  onToggleVideoMute,
  hasOriginalAudio = false,
  hasExistingMusicTrack = false,
  hasNativeAudioConfig = false,
}: TimelineEditorProps) => {
  const trackRef = useRef(null)
  const numberFormatter = useNumberFormatter()
  const audioDurationRef = useRef(selectedAudio?.duration || 0)

  // Update the ref when audio is selected, preserve it when removed
  if (selectedAudio?.duration) {
    audioDurationRef.current = selectedAudio.duration
  }

  const timelineDuration = videoDuration + audioDurationRef.current

  const { frames, isLoadingFrames, hasDefaultAudio, videoRef } = useVideoFrameExtraction({
    videoUrl,
    videoDuration,
  })

  const { isDraggingMusic, handleMusicDragStart, musicActiveLeft, musicActiveWidth } = useMusicTrackDragging({
    musicStartTime,
    audioDuration: selectedAudio?.duration || audioDurationRef.current,
    videoDuration,
    timelineDuration,
    onMusicStartTimeChange,
    onPausePlayback,
    onResumePlayback,
    onTimeChange,
    isPlaying,
  })

  const state = useSliderState({
    numberFormatter,
    value: currentTime,
    onChange: onTimeChange,
    minValue: 0,
    maxValue: videoDuration,
    step: 0.1,
  })

  const { groupProps } = useSlider(
    {
      'aria-label': 'Video timeline',
    },
    state,
    trackRef,
  )

  const handleReplaceAudioClick = () => {
    if (isPlaying) {
      onPausePlayback?.()
    }

    onReplaceAudio()
  }

  const handleRemoveAudioClick = () => {
    if (isPlaying) {
      onPausePlayback?.()
    }

    onRemoveAudio()
  }

  // Show mute button if:
  // 1. The design uses the new audio-mux config (native audio is always toggleable), OR
  // 2. There is an original audio track in metadata, OR
  // 3. We detect an audio track (hasDefaultAudio) AND there is no existing music track
  const shouldShowMuteButton = hasNativeAudioConfig || hasOriginalAudio || (hasDefaultAudio && !hasExistingMusicTrack)

  return (
    <div className={Styles.timelineEditor}>
      <video ref={videoRef} src={videoUrl} preload="metadata" style={{ display: 'none' }} crossOrigin="anonymous" />
      <div className={Styles.timelineTracks}>
        <div className={Styles.timelineTrack}>
          <div className={Styles.trackLabel}>
            <div className={Styles.trackMetadata}>
              <Text variant="primary" color="var(--dark-80)">
                Music track
              </Text>
              <Tooltip label={selectedAudio?.title || 'Untitled'}>
                <span className={Styles.trackTitle}>{selectedAudio?.title || 'Untitled'}</span>
              </Tooltip>
            </div>

            {selectedAudio && (
              <div className={Styles.trackActions}>
                <Tooltip label="Replace music track" placement="bottom-start">
                  <IconButton
                    size="xs"
                    variant="ghost"
                    onPress={handleReplaceAudioClick}
                    icon={Edit1}
                    color="var(--dark-100)"
                    aria-label="Replace music track"
                  />
                </Tooltip>

                <Tooltip label="Remove music track" placement="bottom-start">
                  <IconButton
                    size="xs"
                    variant="ghost"
                    onPress={handleRemoveAudioClick}
                    icon={Trash2}
                    color="var(--dark-100)"
                    aria-label="Remove music track"
                  />
                </Tooltip>
              </div>
            )}
          </div>
          <div className={Styles.trackContent}>
            <div className={Styles.trackFullRow}>
              {selectedAudio ? (
                <div
                  className={cx(Styles.audioBlock, {
                    [Styles.dragging]: isDraggingMusic,
                  })}
                  style={{
                    left: `${musicActiveLeft}%`,
                    width: `${musicActiveWidth}%`,
                  }}
                  onMouseDown={handleMusicDragStart}
                >
                  <Waveform />
                </div>
              ) : (
                <div className={Styles.addMusicButtonContainer}>
                  <Button size="xs" variant="ghost" onPress={onReplaceAudio} endIcon={Plus} color="var(--dark-80)">
                    Add music track
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={Styles.timelineTrack}>
          <div className={Styles.trackLabel}>
            <Text variant="primary" color="var(--dark-80)">
              Video
            </Text>

            {shouldShowMuteButton && onToggleVideoMute && (
              <IconButton
                className={Styles.muteButton}
                onPress={onToggleVideoMute}
                icon={isVideoMuted ? VolumeOff : VolumeOn}
                variant="ghost"
                size="xs"
                color="var(--dark-100)"
                aria-label={isVideoMuted ? 'Unmute video' : 'Mute video'}
              />
            )}
          </div>
          <div className={Styles.trackContent}>
            <div className={Styles.trackFullRow}>
              <div
                className={Styles.videoBlock}
                style={{
                  left: '0%',
                  width: `${Math.round((videoDuration / timelineDuration) * 100)}%`,
                }}
              >
                <div className={Styles.videoFramesTrack}>
                  {isLoadingFrames ? (
                    <div className={Styles.loadingFrames}>Loading...</div>
                  ) : (
                    frames.map((frame, index) => (
                      <img key={index} src={frame} alt={`Frame ${index}`} className={Styles.videoFrame} />
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div {...groupProps} className={Styles.playheadContainer}>
          <div
            ref={trackRef}
            className={Styles.playheadTrack}
            style={{
              width: `${Math.round((videoDuration / timelineDuration) * 100)}%`,
            }}
          >
            <Thumb state={state} trackRef={trackRef} index={0} />
          </div>
        </div>
      </div>
    </div>
  )
}
